import { expandInspectionUrl } from './shortenUrl';

export interface ParsedInspectionInput {
  inspectionId: string | null;
  isValid: boolean;
  inputType: 'id' | 'fullUrl' | 'shortUrl' | 'invalid';
  error?: string;
}

/**
 * Parses various inspection input formats and extracts the inspection ID
 * Supports:
 * - Direct inspection ID: "550e8400-e29b-41d4-a716-446655440000"
 * - Full URLs: "https://domain.com/inspection/550e8400-e29b-41d4-a716-446655440000"
 * - Full URLs with result: "https://domain.com/inspection/550e8400-e29b-41d4-a716-446655440000/result"
 * - Short URLs: "/s/shortId" or "https://domain.com/s/shortId"
 */
export const parseInspectionInput = (input: string): ParsedInspectionInput => {
  if (!input || !input.trim()) {
    return {
      inspectionId: null,
      isValid: false,
      inputType: 'invalid',
      error: 'Input is required',
    };
  }

  const trimmedInput = input.trim();

  // UUID regex pattern for validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Check if input is just a UUID (inspection ID)
  if (uuidRegex.test(trimmedInput)) {
    return {
      inspectionId: trimmedInput,
      isValid: true,
      inputType: 'id',
    };
  }

  // Check if input is a shortened URL (starts with /s/ or contains /s/)
  const shortUrlMatch = trimmedInput.match(/(?:https?:\/\/[^\/]+)?\/s\/([^\/\?\#]+)/);
  if (shortUrlMatch) {
    try {
      const shortPath = `/s/${shortUrlMatch[1]}`;
      const expandedUrl = expandInspectionUrl(shortPath);

      // Extract inspection ID from expanded URL
      const expandedMatch = expandedUrl.match(/\/inspection\/([^\/\?\#]+)/);
      if (expandedMatch && uuidRegex.test(expandedMatch[1])) {
        return {
          inspectionId: expandedMatch[1],
          isValid: true,
          inputType: 'shortUrl',
        };
      } else {
        return {
          inspectionId: null,
          isValid: false,
          inputType: 'invalid',
          error: 'Invalid shortened URL format',
        };
      }
    } catch (error) {
      return {
        inspectionId: null,
        isValid: false,
        inputType: 'invalid',
        error: 'Failed to expand shortened URL',
      };
    }
  }

  // Check if input is a full inspection URL
  const fullUrlMatch = trimmedInput.match(/(?:https?:\/\/[^\/]+)?\/inspection\/([^\/\?\#]+)(?:\/result)?/);
  if (fullUrlMatch) {
    const extractedId = fullUrlMatch[1];

    if (uuidRegex.test(extractedId)) {
      return {
        inspectionId: extractedId,
        isValid: true,
        inputType: 'fullUrl',
      };
    } else {
      return {
        inspectionId: null,
        isValid: false,
        inputType: 'invalid',
        error: 'Invalid inspection ID in URL',
      };
    }
  }

  // If none of the patterns match
  return {
    inspectionId: null,
    isValid: false,
    inputType: 'invalid',
    error: 'Invalid format. Please enter an inspection ID, full URL, or shortened URL',
  };
};
